import type { ActivityEvent } from '@workflow/contracts'
import { ROLE_LABEL } from '@/features/people/roles'
import { ASSIGNMENT_STATE, JOB_STATUS } from '@/features/jobs/jobsStatus'
import { CLIENT_STATUS } from '@/features/clients/clientStatus'

/**
 * The words for what happened. The audit's action is the studio's own
 * vocabulary (`job.status_changed`); the feed says what that *means* — who
 * did it, to what, and how it ended. Unknown actions fall through as their
 * raw selves: the trail never lies, even before it has a phrase.
 */
export function describeActivity(event: ActivityEvent): {
  pre: string
  subject?: string | null
  post?: string
  raw?: boolean
} {
  const data = (event.data ?? {}) as Record<string, unknown>
  const role = typeof data.role === 'string' ? (ROLE_LABEL[data.role as keyof typeof ROLE_LABEL] ?? data.role) : null

  switch (event.action) {
    // ---- Accounts -----------------------------------------------------------
    case 'account.signed_in':
      return { pre: data.via === 'google' ? 'signed in with Google' : 'signed in' }
    case 'account.signed_out':
      return { pre: 'signed out' }
    case 'account.sign_in_denied':
      return { pre: 'refused a sign-in for', subject: event.subject }
    case 'member.whitelisted':
      return { pre: 'whitelisted', subject: event.subject, post: role ? `as ${role.toLowerCase()}` : undefined }
    case 'member.reopened':
      return { pre: 're-opened the request from', subject: event.subject }
    case 'member.approved':
      return { pre: 'approved', subject: event.subject }
    case 'member.rejected':
      return { pre: 'rejected', subject: event.subject }
    case 'member.suspended':
      return { pre: 'suspended', subject: event.subject }
    case 'member.revoked':
      return { pre: 'revoked access for', subject: event.subject }
    case 'member.restored':
      return { pre: 'restored', subject: event.subject }
    case 'member.role_changed':
      return { pre: 'made', subject: event.subject, post: role ? `a ${role.toLowerCase()}` : 'a different role' }
    case 'profile.submitted':
      return { pre: 'submitted their profile for review' }
    case 'profile.updated':
      return { pre: 'updated their profile' }
    case 'authz.denied':
      return { pre: 'was refused', subject: event.subject }

    // ---- Clients ------------------------------------------------------------
    case 'client.created':
      return { pre: 'opened the client', subject: event.subject }
    case 'client.updated':
      return { pre: 'edited the record of', subject: event.subject }
    case 'client.status_changed': {
      const to = typeof data.to === 'string' ? CLIENT_STATUS[data.to as keyof typeof CLIENT_STATUS]?.label.toLowerCase() : null
      return { pre: 'marked', subject: event.subject, post: to ? `as ${to}` : undefined }
    }
    case 'client.archived':
      return { pre: 'archived', subject: event.subject }
    case 'client.restored':
      return { pre: 'brought back', subject: event.subject }
    case 'client.deleted':
      return { pre: 'deleted the client', subject: event.subject }

    // ---- Jobs ---------------------------------------------------------------
    case 'job.created':
      return { pre: 'opened the job', subject: event.subject }
    case 'job.updated':
      return { pre: 'edited', subject: event.subject }
    case 'job.status_changed': {
      const to = typeof data.to === 'string' ? JOB_STATUS[data.to as keyof typeof JOB_STATUS]?.label.toLowerCase() : null
      return { pre: 'moved', subject: event.subject, post: to ? `to ${to}` : undefined }
    }
    // Milestones left the product; their past events still read in the feed.
    case 'job.milestone_added':
      return { pre: 'added the milestone', subject: event.subject }
    case 'job.milestone_updated':
      return { pre: 'edited the milestone', subject: event.subject }
    case 'job.milestone_status_changed': {
      const to = typeof data.to === 'string' ? data.to.replace('_', ' ') : null
      return { pre: 'moved the milestone', subject: event.subject, post: to ? `to ${to}` : undefined }
    }

    // ---- Assignments ----------------------------------------------------------
    case 'assignment.created':
      return { pre: 'staffed', subject: event.subject }
    case 'assignment.updated':
      return { pre: 'updated the assignment of', subject: event.subject }
    case 'assignment.completion_changed': {
      const to = typeof data.to === 'string' ? ASSIGNMENT_STATE[data.to as keyof typeof ASSIGNMENT_STATE]?.label.toLowerCase() : null
      return { pre: 'moved', subject: event.subject, post: to ? `to ${to}` : undefined }
    }

    // ---- Notes ---------------------------------------------------------------
    case 'note.added':
      return { pre: data.visibility === 'shared' ? 'added a shared note on' : 'added a note on', subject: event.subject }
    case 'note.updated':
      return { pre: 'edited a note on', subject: event.subject }

    // ---- Announcements -------------------------------------------------------
    case 'announcement.published':
      return { pre: 'published the announcement', subject: typeof data.title === 'string' ? data.title : event.subject }
    case 'announcement.updated':
      return { pre: 'edited an announcement', subject: event.subject }
    case 'announcement.poll_closed':
      return { pre: 'closed the poll on an announcement', subject: event.subject }
    case 'announcement.archived':
      return { pre: 'archived an announcement', subject: event.subject }
    case 'announcement.restored':
      return { pre: 'restored an announcement', subject: event.subject }

    default:
      return { pre: event.action, raw: true }
  }
}
